import { createSelector } from 'reselect';
import { sortArrayByProfileType, CONST_PROFILE_TYPE_SINGLE } from '../helpers/user';
import { defaultLocaleSelector } from './app';

const stateSelector = state => state.user;

export const documentsSelector = createSelector(stateSelector, state => state.documents);
export const documentTypesSelector = createSelector(stateSelector, state => state.documentTypes);
export const documentByServiceSelector = createSelector(stateSelector, state => state.documentByService);
export const meSelector = createSelector(stateSelector, state => state.me);
export const userIdSelector = createSelector([meSelector], me => (me && me.id) || null);
export const localeSelector = createSelector(
    [meSelector, defaultLocaleSelector],
    (me, defaultLocale) =>
        (me && me.locale) ||
        defaultLocale ||
        (navigator && navigator.language && navigator.language.replace('-', '_')) ||
        'en_GB'
);

export const userCurrencySelector = createSelector(meSelector, me => (me && me.profile && me.profile.currency) || '');

export const entityIdSelector = createSelector([meSelector], user => {
    let { entityId } = user;
    if (!entityId) {
        const personalProfile =
            user.profiles && user.profiles.find(profile => profile.type === CONST_PROFILE_TYPE_SINGLE);
        entityId = personalProfile && personalProfile.entityId;
    }
    return entityId;
});
export const paymentDetailsSelector = createSelector(stateSelector, state => state.paymentDetails);
export const paymentDetailsBusinessSelector = createSelector(stateSelector, state => state.paymentDetailsBusiness);
// paymentMethodDetails.token is the PSP's stored-method id: on Adyen it equals
// the `id` of the matching entry in paymentMethodsResponse.storedPaymentMethods.
export const mopTokenSelector = createSelector(
    stateSelector,
    state => (state.paymentDetails && state.paymentDetails.token) || null
);
export const fetchingSelector = createSelector(stateSelector, state => state.fetching);
export const profileTabSelector = createSelector(stateSelector, state => state.profileTab);
export const updatingSelector = createSelector(stateSelector, state => state.updating);
export const servicesSelector = createSelector(stateSelector, state => state.services);
export const userServicesWithStatusSelector = createSelector(stateSelector, state => state.userProfilesWithServices);

export const listProfileSelector = createSelector(meSelector, state =>
    state && state.profiles && Array.isArray(state.profiles) ? sortArrayByProfileType(state.profiles) : []
);
export const subscriptionsSelector = createSelector(stateSelector, state => state.subscriptions.list);
export const subscriptionsFetchingSelector = createSelector(stateSelector, state => state.subscriptions.fetching);
export const subscriptionsUpdatingSelector = createSelector(stateSelector, state => state.subscriptions.updating);
export const mobilityPlansSelector = createSelector(stateSelector, state => state.mobilityPlans.list);
export const mobilityPlansFetchingSelector = createSelector(stateSelector, state => state.mobilityPlans.fetching);
export const profileIdSelector = createSelector(stateSelector, state => state.me.profile && state.me.profile.profileId);
export const profileSelector = createSelector(stateSelector, state => state.me.profile);
export const billingGroupSelector = createSelector(stateSelector, state => state.me.profile.billingGroup);

export const subscriptionSelector = createSelector(stateSelector, state => state.subscriptions.list[0] || {});

export const subscriptionIdSelector = createSelector(subscriptionSelector, subscription => subscription.id);

export const subscribedPlanIdSelector = createSelector(subscriptionSelector, subscription => subscription.planId);

export const subscribedPlanExpiryDateSelector = createSelector(
    subscriptionSelector,
    subscription => subscription.expiryDate
);

export const subscribedPlanUnsubscribeDateSelector = createSelector(
    subscriptionSelector,
    subscription => subscription.unsubscribeDate
);

export const isSubscriptionPaymentPendingSelector = createSelector(
    subscriptionSelector,
    subscription => subscription.activationDate === null
);

export const subscribedPlanNameSelector = createSelector(
    [subscribedPlanIdSelector, mobilityPlansSelector],
    (subscribedPlanId, plans) => {
        if (!subscribedPlanId) {
            return '';
        }

        const myPlan = plans.find(plan => plan.id === subscribedPlanId);

        if (!myPlan) {
            return '';
        }

        return myPlan.name;
    }
);

export const defaultPricingSelector = createSelector(stateSelector, state => state.defaultPricing);

export const updatePaymentMethodSuccessSelector = createSelector(
    stateSelector,
    state => state.updatePaymentMethodSuccess
);

export const resetPasswordSuccessSelector = createSelector(stateSelector, state => state.resetPasswordSuccess);

export const userOngoingTripsSelector = createSelector(stateSelector, state => state.ongoingTrips);
export const userOngoingTripsLoadingSelector = createSelector(stateSelector, state => state.ongoingTrips.loading);
export const userOngoingTripsIdsSelector = createSelector(stateSelector, state => state.ongoingTrips.ids);
export const userOngoingTripsDataSelector = createSelector(stateSelector, state => state.ongoingTrips.data);
export const userOngoingTripsByIdSelector = createSelector(
    [userOngoingTripsDataSelector, (_, id) => id],
    (data, id) => data[id]
);
